import { useEffect, useRef, useCallback } from "react";
import { edgeFunctionUrl } from "@/lib/edgeFunctionUrl";

interface MinimalScript {
  script_head: string;
  script_body: string;
}

const TrackingScripts = () => {
  const injectedRef = useRef<Node[]>([]);
  const didInjectRef = useRef(false);
  const didFetchRef = useRef(false);

  const cleanup = useCallback(() => {
    injectedRef.current.forEach((el) => el.parentNode?.removeChild(el));
    injectedRef.current = [];
    didInjectRef.current = false;
  }, []);

  useEffect(() => {
    if (didFetchRef.current) return;
    // Skip tracking on admin routes — not needed and adds heavy 3rd-party JS
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      return;
    }
    didFetchRef.current = true;

    const injectHTML = (html: string, target: HTMLElement) => {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html;

      wrapper.querySelectorAll("script").forEach((oldScript) => {
        const newScript = document.createElement("script");
        Array.from(oldScript.attributes).forEach((attr) =>
          newScript.setAttribute(attr.name, attr.value)
        );
        if (oldScript.textContent) {
          newScript.textContent = oldScript.textContent;
        }
        target.appendChild(newScript);
        injectedRef.current.push(newScript);
      });

      wrapper.querySelectorAll(":not(script)").forEach((node) => {
        const cloned = node.cloneNode(true);
        target.appendChild(cloned);
        injectedRef.current.push(cloned);
      });
    };

    const injectScripts = (scripts: MinimalScript[]) => {
      if (didInjectRef.current || !scripts.length) return;
      didInjectRef.current = true;
      scripts.forEach((s) => {
        if (s.script_head?.trim()) injectHTML(s.script_head, document.head);
        if (s.script_body?.trim()) injectHTML(s.script_body, document.body);
      });
    };

    const fetchAndInject = async () => {
      try {
        const res = await fetch(
          edgeFunctionUrl("serve-tracking-scripts"),
          { headers: { "Content-Type": "application/json" } }
        );
        if (!res.ok) return;
        const { scripts } = await res.json();
        // GTM agora carrega cedo, direto no index.html — nunca injetar de novo
        const nonGtmScripts = ((scripts ?? []) as MinimalScript[]).filter(
          (s) =>
            !`${s.script_head ?? ""}${s.script_body ?? ""}`.includes(
              "googletagmanager.com"
            )
        );
        if (!nonGtmScripts.length) return;

        const doInject = () => injectScripts(nonGtmScripts);

        if (document.readyState === "complete") {
          setTimeout(doInject, 1500);
        } else {
          const fallback = setTimeout(doInject, 3000);
          window.addEventListener("load", () => {
            clearTimeout(fallback);
            setTimeout(doInject, 1500);
          }, { once: true });
        }
      } catch {
        // silently fail — tracking is non-critical
      }
    };

    fetchAndInject();

    return cleanup;
  }, [cleanup]);

  return null;
};

export default TrackingScripts;
